import { useTranslation } from "react-i18next";
import {useContext, useEffect, useState} from "react";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import {defaultDrug, hospitalLanguages} from "../../resources/json/jsons";
import { Consts } from "../pages/consts";
import IntakesFrequencyAndDosageUnit from "../patientInit/drugs/IntakesFrequencyAndDosageUnit";
import IntakeTypeRadioGroup from "../drugs/IntakeTypeRadioGroup";
import TimingTypeRadioGroup from "../patientInit/drugs/TimingTypeRadioGroup";
import InitContext from "../pages/InitContext";
import SpecifyNewDrug from "../drugs/SpecifyNewDrug";
import StrengthAndStartDate from "./StrengthAndStartDate";

const Drug = ({onSaveDrug, onHandleAddDrug, allProductNames, allSubstances, patDrugs, language }) => {
  const { t: t } = useTranslation();
  const [selectedDrug, setSelectedDrug] = useState({...defaultDrug, intakes: Consts.DEFAULT_DAY_TIME_INTAKES});
  const [startDate, setStartDate] = useState("");
  const { error, loading, getDrugBySubstancesJson } = useMmcService();
  const [strengthUnit, setStrengthUnit] = useState("");
  const [strength, setStrength] = useState(0);
  const [messageText, setMessageText] = useState("");
  const [intakesCompleted, setIntakesCompleted] = useState(false);
  const context = useContext(InitContext);

  useEffect(() => {
    resetFields();
  }, []);

  const resetFields = () => {
    setMessageText("");
    setSelectedDrug(defaultDrug);
    setStrength(0);
    setStrengthUnit("");
    setStartDate("");
    setIntakesCompleted(false);
  }

  const handleStartDate = (value) => {
    setStartDate(value);
    setSelectedDrug({ ...selectedDrug, startDate: value });
  };

  const handleStrength = (value) => {
    setStrength(value);
    setSelectedDrug({ ...selectedDrug, strength: value });
  };

  const handleStrengthUnit = (value) => {
    setStrengthUnit(value);
    setSelectedDrug({ ...selectedDrug, strengthUnit: value });
  };

  const cleanIntakesAndSaveDrug = () => {
    const cleanedIntakes = selectedDrug?.intakes?.filter(intake =>
      intake?.intakeTime !== "" && !isNaN(intake?.dosage ) && intake?.dosage > 0);

    onSaveDrug({...selectedDrug, intakes: cleanedIntakes});
  };

  const handleSetFreeDrugDetails = (detailsName, value) => {
    setMessageText("");

    if(detailsName === Consts.PRODUCT){
      setSelectedDrug({ ...selectedDrug, productName: value });
    } else if(detailsName === Consts.SUBSTANCE){
      const cleanedSubstanceArr = value?.split(",")?.map(item => item.trim())?.filter(item => item !== "");

      getDrugBySubstancesJson(context?.token, cleanedSubstanceArr.join("&&&"), language)
        .then(resultDrug => {
          if(resultDrug?.atc){
            setSelectedDrug({ ...selectedDrug, activeSubstance: cleanedSubstanceArr, atc: resultDrug?.atc,
              abda: resultDrug?.abda});
          } else {
            setSelectedDrug({ ...selectedDrug, activeSubstance: cleanedSubstanceArr});
          }
        }
      ).catch((err) => {
        console.error("handleOtherSubstance, err", err.message);
        setSelectedDrug({ ...selectedDrug, activeSubstance: cleanedSubstanceArr });
      })
    }
  }

  const handleSetSelectedDrug = (newDrug) => {
    setMessageText("");
    setSelectedDrug({...newDrug, intakeType: selectedDrug?.intakeType });
  }

  const handleSetIntakeType = (intakeTypeObj) => {
    setSelectedDrug({...selectedDrug, ...intakeTypeObj});
  }

  const errorMessageComp = error || (messageText && <ErrorMessage message={messageText} />);
  const spinner = loading && <Spinner />;

  const basicDataOk = (selectedDrug?.productName !== "" || selectedDrug?.activeSubstance !== "")
    && selectedDrug?.startDate && selectedDrug?.startDate !== ""
    && selectedDrug?.strength && selectedDrug?.strength > 0
    && selectedDrug?.strengthUnit && selectedDrug?.strengthUnit !== "";

  const enableRegularly = basicDataOk
    && selectedDrug?.timingType !== ""
    && selectedDrug?.dosageUnit && selectedDrug?.dosageUnit?.dosageUnit !== ""
    && intakesCompleted;

  const enableSubmit = selectedDrug?.intakeType === Consts.REGULARLY ? enableRegularly : basicDataOk;

  return (
    <div className="w3-modal" style={{ display: Consts.BLOCK}}>
      <div className="w3-modal-content w3-card-4 w3-animate-zoom form-panel_not_init dialog-medium">
        <header className="w3-container w3-padding-small w3-center">
          <p>{t("please_enter_your_medicine_data")}</p>
        </header>
        <div className="w3-container">
          <SpecifyNewDrug
            patDrugs={patDrugs}
            allProductNames={allProductNames}
            allSubstances={allSubstances}
            onSetSelectedDrug={handleSetSelectedDrug}
            resetFields={resetFields}
            setMessageText={setMessageText}
            setStrength={setStrength}
            setStrengthUnit={setStrengthUnit}
            handleSetFreeDrugDetails={handleSetFreeDrugDetails}
            init={false}
            language={language}
          />
          {errorMessageComp}
          {spinner}
          <StrengthAndStartDate
            strength={strength}
            onHandleStrength={handleStrength}
            strengthUnit={strengthUnit}
            onHandleStrengthUnit={handleStrengthUnit}
            startDate={startDate}
            onHandleStartDate={handleStartDate}
          />
        </div>
        <div className="w3-container">
          <div className="w3-row w3-padding-small radiogroup-left_aligned">
            <IntakeTypeRadioGroup onSetIntakeType={ handleSetIntakeType }/>
          </div>
          { selectedDrug?.intakeType === Consts.REGULARLY && (
            <>
              <IntakesFrequencyAndDosageUnit
                selectedDrug={ selectedDrug }
                setSelectedDrug={ setSelectedDrug }
              />
              <TimingTypeRadioGroup
                selectedDrug={ selectedDrug }
                setSelectedDrug={ setSelectedDrug }
                setIntakesCompleted={ setIntakesCompleted }
              />
            </>
          ) }
        </div>
        <footer className="w3-container w3-padding-small">
          <p className="w3-right">
            <button
              className="w3-btn w3-round-xlarge w3-theme-d5"
              onClick={cleanIntakesAndSaveDrug}
              disabled={!enableSubmit}
            >
              {t("submit")}
            </button>
            <button
              onClick={() => onHandleAddDrug(false) }
              className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin"
              type="text"
            >
              {t("cancel")}
            </button>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Drug;
